import {Component, EventEmitter, Input, OnChanges, Output, TemplateRef, ViewChild} from '@angular/core';
import {ScriptConfiguration, ScriptingLanguageType} from '../../cpq-scripting-codemirror/cpq-scripting.model';
import {CellType, ColumnType, IColumn} from '../../common/interfaces/IColumn.interface';
import {DataTableFilter, RowChanges} from '../../common/models/DataTableFilter.model';
import {TranslationService} from '../../common/services/translation.service';
import {MatDialog} from '@angular/material/dialog';
import {CpqDatatableComponent} from '../../cpq-datatable/cpq-datatable.component';
import {IScriptHistoryService} from '../../common/interfaces/IScriptHistoryService.interface';
import {ScriptHistoryRecord, ScriptRecordCompareRequest} from '../../common/models/ScriptHistory.model';
import {AlertDialogComponent} from '../../dialogs/alert-dialog/alert-dialog.component';
import {CompareData} from '../../cpq-text-compare/text-compare.model';

@Component({
    selector: 'cpq-script-history-datatable',
    templateUrl: './script-history-datatable.component.html',
    styleUrls: ['./script-history-datatable.component.scss'],
    standalone: false
})
export class ScriptHistoryDatatableComponent implements OnChanges {

  @Input('scriptHistoryService') scriptHistoryService: IScriptHistoryService;
  @Input('scriptId') scriptId: number;
  @Input('scriptConfig') scriptConfig: ScriptConfiguration;
  @Input('languageType') languageType: ScriptingLanguageType = ScriptingLanguageType.IronPython;


  @Output('showRecordDetails') showRecordDetails: EventEmitter<{scriptRecordId: number, scriptRecordVersionNumber: number}> = new EventEmitter<{scriptRecordId: number, scriptRecordVersionNumber: number}>();
  @Output('showComparedRecords') showComparedRecords: EventEmitter<{originalObject: CompareData, compareObject: CompareData}> = new EventEmitter<{originalObject: CompareData, compareObject: CompareData}>();

  @ViewChild('editTmpl', { static: true }) editTmpl: TemplateRef<any>;
  @ViewChild('buttonHeader', { static: true }) buttonHeader: TemplateRef<any>;
  @ViewChild('datatable') datatable: CpqDatatableComponent;

  public columns = new Array<IColumn>();
  public filter: DataTableFilter;
  public isHistoryTableInit: boolean;
  public selectedRecords: Array<ScriptHistoryRecord>;
  public guid: string = "shared_script_history_datatable_component";

  constructor(public translationService: TranslationService,
    private dialog: MatDialog) { }

  ngOnChanges(): void {
    if (this.scriptId > 0) {
      this.initScriptHistoryColumns();
      this.scriptHistoryService.scriptId = this.scriptId;
      this.selectedRecords = [];
      this.showComparedRecords.emit(null);
      this.showRecordDetails.emit(null);
      this.isHistoryTableInit = true;
      if(this.datatable) {
        this.datatable.filterTable(true);
      }
    }
  }

  public showDetails(scriptRecordId: number, scriptRecordVersionNumber: number) {
    this.showRecordDetails.emit({scriptRecordId: scriptRecordId, scriptRecordVersionNumber: scriptRecordVersionNumber});
  }

  private initScriptHistoryColumns() {
    this.filter = new DataTableFilter();
    this.filter.placeholder = 'QuoteList.QuickFilter';

    this.columns = [
      {
        prop: 'edit',
        cellTemplate: this.editTmpl,
        cellType: CellType.actions,
        name: this.translationService.translate('Msg.View'),
        visible: true,
        sortable: false,
        maxWidth: 120,
        minWidth: 120
      },
      {
        prop: 'versionNumber',
        name: this.translationService.translate('Admin.ProductVersions.VersionNumber'),
        visible: true,
        sortable: false
      },
      {
        prop: 'created',
        name: this.translationService.translate('NetSuite.createdDate'),
        visible: true,
        sortable: false
      },
      {
        prop: 'changedBy',
        name: this.translationService.translate('Admin.Deployment.CreatedBy'),
        visible: true,
        sortable: false
      },
      {
        cellType: CellType.checkbox,
        name: this.translationService.translate('Admin.Responsive.Compare'),
        prop: 'isSelected',
        visible: true,
        editable: true,
        sortable: false,
        headerTemplate: this.buttonHeader
      }
    ];
    if(this.languageType == ScriptingLanguageType.IronPython) {
      let descriptionColumnIndex = this.columns.indexOf(this.columns.find(column => column.prop === 'versionNumber')) + 1;
      this.columns.splice(descriptionColumnIndex, 0,
        {
          prop: 'description',
          name: this.translationService.translate('AddEditSystemEventScript.Description'),
          visible: true,
          sortable: false

        }
      )
    }

  }

  public onRowChanged(rowChanges: RowChanges) {
    this.selectedRecords = rowChanges.modifiedRows.filter(x => x.isSelected);
  }

  public compare() {
    if (!this.selectedRecords || this.selectedRecords.length !== 2) {
      this.dialog.open(AlertDialogComponent, {
        data: {
          content: this.translationService.translate('Versions.SelectExactlyTwoVersionToCompare')
        }
      });
    } else {
        const firstRecord: ScriptHistoryRecord = this.selectedRecords[0];
        const secondRecord: ScriptHistoryRecord = this.selectedRecords[1];
        const originalObject = new CompareData();
        const compareObject = new CompareData();

        if (firstRecord.versionNumber < secondRecord.versionNumber) {
          originalObject.title = this.getTitle(firstRecord);
          originalObject.content = firstRecord.content;
          compareObject.title = this.getTitle(secondRecord);
          compareObject.content = secondRecord.content;
        } else {
          originalObject.title = this.getTitle(secondRecord);
          originalObject.content = secondRecord.content;
          compareObject.title = this.getTitle(firstRecord);
          compareObject.content = firstRecord.content;
        }
        this.showComparedRecords.emit({originalObject: originalObject, compareObject: compareObject})
    }
  }

  private getTitle(scriptRecord: ScriptHistoryRecord): string {
    return `Version: ${scriptRecord.versionNumber}, ${scriptRecord.changedBy} ${scriptRecord.created}`;
  }

}
